import clsx from "clsx"

import { HoverTip } from "components/tooltips/HoverTip"

// SOFT-559 (T10): a small, CLIENT-FACING badge telling the operator whether the Balance figures are
// final or still provisional. This admin panel is used by our clients, so the copy is deliberately
// neutral and free of internal architecture terms — no "pre-migration", "MNT shadow" or
// "financial-service". Absent `source` (non-microservice tenant) renders nothing.
//
// ─── Where `source` comes from (BACKEND, not this repo) ──────────────────────────────────────────
// The FE only RENDERS the `data_source` the admin API returns. Which value it is depends ENTIRELY on
// two BACKEND (`core-api`) env vars — they do NOT exist in this frontend's `.env`, and the FE cannot
// influence them:
//
//   • FS_BALANCE_REPORTS_ENABLED (default OFF) — the per-tenant kill switch for serving Balance
//     reports from the financial-service ledger. While OFF, every window is served from the MNT
//     shadow and marked `mnt-shadow`.
//   • FS_LEDGER_GO_LIVE (per-tenant instant) — when the FS ledger started holding authoritative data.
//     Unset/invalid → `mnt-shadow` (the backend never claims FS authority on an unknown boundary).
//
// Resulting `data_source` (see core-api `services/admin/balance/balance-data-source.ts`):
//   • non-microservice tenant                                   → field absent (badge hidden)
//   • window entirely BEFORE go-live                            → `mnt`  (final for the pre-FS era)
//   • window in the FS era AND FS_BALANCE_REPORTS_ENABLED=true  → `fs`   (authoritative/reconciled)
//   • flag OFF / go-live unset / window straddles the boundary  → `mnt-shadow` (provisional default)
//
// So a persistent "Provisional" on a live tenant is EXPECTED until finance flips
// FS_BALANCE_REPORTS_ENABLED=true and sets FS_LEDGER_GO_LIVE for that tenant on the backend — it is
// NOT a bug here and NOT a sign the microservices are down.

// `fs` and `mnt` are both authoritative (FS-reconciled / final for the pre-FS era); `mnt-shadow` is
// the provisional default. Clients only need the final-vs-provisional distinction.
const STYLES: Record<string, string> = {
  fs: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  mnt: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  "mnt-shadow": "bg-gray-500/10 text-gray-500 dark:text-gray-300 border-gray-400/30"
}

const LABELS: Record<string, string> = {
  fs: "Reconciled",
  mnt: "Reconciled",
  "mnt-shadow": "Provisional"
}

// Client-safe explanations (NOT the internal backend note).
const TOOLTIPS: Record<string, string> = {
  fs: "Financial figures are final.",
  mnt: "Financial figures are final.",
  "mnt-shadow": "Provisional figures — pending final reconciliation."
}

type Props = {
  source?: string
}

export const DataSourceBadge = ({ source }: Props) => {
  if (!source) return null

  const badge = (
    <span className={clsx("rounded-md border px-2 py-0.5 text-xs font-medium", STYLES[source] ?? STYLES["mnt-shadow"])}>
      {LABELS[source] ?? "Provisional"}
    </span>
  )

  const tip = TOOLTIPS[source]
  return tip ? <HoverTip text={tip}>{badge}</HoverTip> : badge
}
